import { useEffect, useState } from 'react'
import ApiRecommend from '@/components/api/ApiRecommend'
import { PageSizeEnum } from '@/constants/page-size.enum'
import { RequestStatusEnum } from '@/constants/request-status.enum'
import useIntersectionObserver from '@/containers/hooks/useIntersectionObserver'
import { MODEL_TYPE } from '@/containers/models/base/modelize'
import { UserContainer } from '@/containers/users/UserContainer'
import { GraphQLProductType } from '@/utils/product/product-card.util'
import { encodeId } from '@/utils/utilApi'

interface FrequentlyBoughtProductProps {
  productId: number
  boughtCount: number
  product: GraphQLProductType
}

const useFrequentlyBoughtProductContainerHook = (size = PageSizeEnum.PageSize8, limitItemsCount?: number) => {
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(RequestStatusEnum.Idle)
  const [page, setPage] = useState(0)
  const [isLast, setIsLast] = useState(false)
  const [data, setData] = useState<FrequentlyBoughtProductProps[]>([])
  const { me } = UserContainer.useContainer()

  const { httpRequest } = ApiRecommend()

  const { elementRef } = useIntersectionObserver(() => {
    setPage((prev) => prev + 1)
  })

  const getFrequentlyBoughtProduct = async () => {
    if ((limitItemsCount && data?.length >= limitItemsCount) || isLast) return
    setLoading(true)
    setStatus(RequestStatusEnum.Loading)
    try {
      const { data: result } = await httpRequest.get('/user/frequently-bought/products', {
        params: {
          page,
          size,
        },
      })
      const products = result?.data?.content?.map((data: FrequentlyBoughtProductProps) => {
        return {
          ...data,
          product: {
            ...data.product,
            id: encodeId(MODEL_TYPE.PRODUCT, data.product.id),
          },
        }
      })
      if (result?.data?.last) {
        setIsLast(true)
      }
      setData([...data, ...products])
      setStatus(RequestStatusEnum.Success)
    } catch (error) {
      setIsLast(true)
      setStatus(RequestStatusEnum.Error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!me || isLast) return
    getFrequentlyBoughtProduct()
  }, [me])

  useEffect(() => {
    if (page === 0 || isLast) {
      return
    }
    getFrequentlyBoughtProduct()
  }, [page])

  return {
    loading,
    data,
    status,
    firstData: data?.[0],
    elementRef,
  }
}

export default useFrequentlyBoughtProductContainerHook
